import { FC, useEffect, useState } from "react";
import {
  Checkbox,
  Fieldset,
  Radio,
  RadioGroup,
  Text,
  TextInput,
} from "@mantine/core";
import useQuestionnaireStore from "../../store/useQuestionnaireStore";
import { SurveyQuestion } from "../../types/api";
import { useTranslation } from "react-i18next";

interface Props {
  questionnaire: {
    [key: string]: { answer_ids: number[]; text_answer?: string };
  };
  setDisabled: (value: boolean) => void;
  updateQuestionnaire: (
    key: string,
    value: { answer_ids: number[]; text_answer?: string }
  ) => void;
}

const renderTextInput = (
  placeholder: string,
  condition: boolean,
  onChange: (input: React.ChangeEvent<HTMLInputElement>) => void
) =>
  condition && (
    <TextInput
      mt="md"
      placeholder={placeholder}
      label={placeholder}
      onChange={onChange}
      autoFocus
    />
  );

export const Questionnaire1: FC<Props> = ({
  questionnaire,
  updateQuestionnaire,
  setDisabled,
}) => {
  const { questions } = useQuestionnaireStore();
  const questionData = questions?.for_yes || [];
  const { t } = useTranslation();

  useEffect(() => {
    if (Object.keys(questionnaire).length <= questionData.length) {
      setDisabled(true);
    } else {
      setDisabled(false);
    }
  }, [questionnaire, questionData]);

  const isOther = (key: string) => {
    const question = questionData.find((q) => q.key === key);
    if (!question) return false;

    const otherOption = question.options.find(
      (option) => option.text === "Other" || option.text === "Інше"
    );
    if (!otherOption) return false;

    const userAnswer = questionnaire[key];
    return userAnswer?.answer_ids?.includes(otherOption.id) || false;
  };
  const renderRadioGroup = (question: SurveyQuestion) => {
    const sortedOptions = [...question.options].sort((a, b) => {
      if (a.text === "Other" || a.text === "Інше") return 1;
      if (b.text === "Other" || b.text === "Інше") return -1;
      return 0;
    });

    // Знаходимо ID для "Other", якщо такий варіант існує
    const otherOptionId = question.options.find(
      (opt) => opt.text === "Other" || opt.text === "Інше"
    )?.id;

    return (
      <RadioGroup
        w="100%"
        size="md"
        ta="left"
        label={
          <Text fw="bold" ta="left">
            {question.text}
          </Text>
        }
        onChange={(value) => {
          const selectedId = question.options.find(
            (option) => option.text === value
          )?.id;

          if (selectedId) {
            updateQuestionnaire(question.key, {
              answer_ids: [selectedId],
            });
          }
        }}
      >
        {sortedOptions.map((option) => (
          <Radio
            key={option.text}
            mt="md"
            label={option.text}
            value={option.text}
          />
        ))}

        {isOther(question.key) &&
          otherOptionId !== undefined &&
          renderTextInput(t("please_specify"), true, (input) => {
            updateQuestionnaire(question.key, {
              answer_ids: [otherOptionId],
              text_answer: input.target.value,
            });
          })}
      </RadioGroup>
    );
  };

  const renderCheckboxGroup = (question: SurveyQuestion) => {
    const optionIds = questionnaire[question.key]?.answer_ids || [];

    const sortedOptions = [...question.options].sort((a, b) => {
      if (a.text === "Other" || a.text === "Інше") return 1;
      if (b.text === "Other" || b.text === "Інше") return -1;
      return 0;
    });

    return (
      <Fieldset
        w="100%"
        legend={
          <Text fw="bold" ta="left">
            {question.text}
          </Text>
        }
        variant="unstyled"
      >
        {sortedOptions.map((option) => (
          <Checkbox
            key={option.text}
            mt="md"
            label={option.text}
            value={option.text}
            checked={optionIds.includes(option.id)}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              const updatedOptionIds = [...optionIds];

              if (event.target.checked) {
                updatedOptionIds.push(option.id);
              } else {
                const index = updatedOptionIds.indexOf(option.id);
                if (index > -1) {
                  updatedOptionIds.splice(index, 1);
                }
              }

              updateQuestionnaire(question.key, {
                answer_ids: updatedOptionIds,
              });
            }}
          />
        ))}

        {isOther(question.key) &&
          renderTextInput(t("please_specify"), true, (input) =>
            updateQuestionnaire(question.key, {
              answer_ids: [...optionIds],
              text_answer: input.target.value,
            })
          )}
      </Fieldset>
    );
  };
  const renderLimitedCheckboxGroup = (
    question: SurveyQuestion,
    limit: number
  ) => {
    const handleCheckboxChange = (value: number, checked: boolean) => {
      const currentOptionIds = questionnaire[question.key]?.answer_ids || [];

      const updatedSelected = checked
        ? [...currentOptionIds, value]
        : currentOptionIds.filter((id) => id !== value);

      if (updatedSelected.length > limit) {
        return;
      }

      updateQuestionnaire(question.key, {
        answer_ids: updatedSelected,
      });
    };

    const sortedOptions = [...question.options].sort((a, b) => {
      if (a.text === "Other" || a.text === "Інше") return 1;
      if (b.text === "Other" || b.text === "Інше") return -1;
      return 0;
    });

    return (
      <Fieldset
        w="100%"
        legend={
          <Text fw="bold" ta="left">
            {question.text}
          </Text>
        }
        variant="unstyled"
      >
        {sortedOptions.map((option) => (
          <Checkbox
            key={option.text}
            mt="md"
            label={option.text}
            value={option.text}
            checked={
              (Array.isArray(questionnaire[question.key]?.answer_ids) &&
                questionnaire[question.key]?.answer_ids.includes(option.id)) ||
              false
            }
            onChange={(e) => handleCheckboxChange(option.id, e.target.checked)}
          />
        ))}

        {isOther(question.key) &&
          renderTextInput(t("please_specify"), true, (input) =>
            updateQuestionnaire(question.key, {
              answer_ids: [...(questionnaire[question.key]?.answer_ids || [])],
              text_answer: input.target.value,
            })
          )}
      </Fieldset>
    );
  };

  const renderQuestion = (question: SurveyQuestion) => {
    if (
      question.text.includes("(Select up to 3)") ||
      question.text.includes("(Оберіть до 3 пунктів)")
    ) {
      return renderLimitedCheckboxGroup(question, 3);
    } else if (question.selection_type === "multiple") {
      return renderCheckboxGroup(question);
    } else if (question.selection_type === "single") {
      return renderRadioGroup(question);
    }
    return null;
  };

  return (
    <>
      {questionData.map((question) => (
        <div key={question.key}>{renderQuestion(question)}</div>
      ))}
    </>
  );
};
